/**
 * googleConnectGuide.js — el paso previo a la pantalla de permisos de Google
 * ==========================================================================
 * Quien acaba en `insufficient_scope` no es que rechazara el permiso: es que no
 * vio la casilla de las fichas de empresa, que Google enseña SIN marcar. Por eso
 * ningún botón de «conectar» manda ya directo al OAuth: abre antes un modal con
 * la captura de esa casilla, y es el botón del modal el que navega.
 *
 * Estado global (zustand) y un único `<GoogleConnectGuideDialog />` montado en
 * `App.jsx`, en vez de un diálogo por botón: los montan cinco sitios distintos
 * (`NoActiveLocationBanner` en catorce páginas) y así el modal no se puede
 * olvidar en ninguno.
 *
 * Tres variantes:
 *   - 'antes'        → se abre al pulsar cualquier «conectar». Guarda el `ctaId`
 *                      del botón de ORIGEN y es el que viaja al OAuth, así que la
 *                      conexión se la sigue llevando ese botón (§123), no el
 *                      del modal.
 *   - 'faltaCasilla' → la abre `ConnectionList` al volver del callback con
 *                      `?status=insufficient_scope`. Su botón va directo al
 *                      OAuth: quien la ve ya sabe lo de la casilla.
 *   - 'sinFicha'     → la abre `ConnectionList` al volver con
 *                      `?status=no_business`: la cuenta elegida no gestiona
 *                      ninguna ficha. Su botón borra esa cuenta y abre Google
 *                      con el selector de cuentas (`reintentarConOtraCuenta`).
 */

import { create } from 'zustand';

import {
  disconnectGbusinessConnection,
  getGbusinessConnectUrl,
} from '@/api/gbusinessService';

// Captura real de la pantalla de consentimiento de Google, con la casilla de
// las fichas de empresa SIN marcar — que es exactamente el estado en el que se
// queda quien acaba en `insufficient_scope`. La usan el modal y el banner
// ámbar de `ConnectionList`, así que vive aquí y no en uno de los dos.
//
// Misma convención que el resto de ilustraciones de la app
// (`GeoGridHelpDialog.jsx`, `EmployeeRanking.jsx`): constante de módulo
// apuntando a `public/`, no un import, así la imagen no entra en el bundle.
export const PERMISOS_SCREENSHOT = '/landing-images/google-permisos-checkbox.png';

export const useGoogleConnectGuide = create((set) => ({
  open: false,
  variant: 'antes',
  ctaId: null,
  // El email de la cuenta que se va a REconectar, o `null` en una conexión
  // nueva. Viaja al OAuth como `login_hint` para que Google abra con esa cuenta
  // ya elegida en vez del selector: con el selector delante, «reconectar esta
  // cuenta» podía acabar conectando otra.
  hintEmail: null,
  // La cuenta que volvió SIN FICHAS, en la variante 'sinFicha'. Va aparte de
  // `hintEmail` porque aquí se busca lo contrario: que Google NO la elija.
  emptyEmail: null,
  close: () => set({ open: false }),
}));

/**
 * Abre el paso previo al OAuth. Sustituye a
 * `window.location.href = getGbusinessConnectUrl(ctaId)` en los botones.
 *
 * @param {string} ctaId Id del botón de origen (meta `connect` en el registro).
 * @param {string|null} [hintEmail] Cuenta a reconectar, si es una reconexión.
 */
export function abrirGuiaGoogle(ctaId, hintEmail = null) {
  useGoogleConnectGuide.setState({
    open: true, variant: 'antes', ctaId, hintEmail, emptyEmail: null,
  });
}

/** Abre el aviso de «falta una casilla» tras un `insufficient_scope`.
 * @param {string|null} [hintEmail] La cuenta que volvió sin la casilla.
 */
export function abrirFaltaCasilla(hintEmail = null) {
  useGoogleConnectGuide.setState({
    open: true, variant: 'faltaCasilla', ctaId: null, hintEmail, emptyEmail: null,
  });
}

/** Abre el aviso de «esta cuenta no tiene ninguna ficha» tras un `no_business`.
 * @param {string|null} [emptyEmail] La cuenta que volvió sin fichas.
 */
export function abrirSinFicha(emptyEmail = null) {
  useGoogleConnectGuide.setState({
    open: true, variant: 'sinFicha', ctaId: null, hintEmail: null, emptyEmail,
  });
}

/**
 * «Probar con otra cuenta»: borra la conexión vacía y manda a Google con el
 * selector de cuentas. La usan el modal 'sinFicha' y el banner rojo de
 * `ConnectionList`, cada uno con su id.
 *
 * Se borra ANTES de ir a Google y no al volver: si el usuario elige otra
 * cuenta, la vacía ya no pinta nada en la lista; y si vuelve a elegir la
 * misma, el callback la recrea igual (upsert). El borrado es best-effort: si
 * falla, se sigue a Google igual — una cuenta vacía de más en la lista es
 * mucho menos grave que un botón que no hace nada.
 *
 * @param {string|null} emptyEmail La cuenta sin fichas, o `null` si no se sabe.
 * @param {string} ctaId Id del botón (meta `connect`): viaja al OAuth.
 */
export async function reintentarConOtraCuenta(emptyEmail, ctaId) {
  if (emptyEmail) {
    try {
      await disconnectGbusinessConnection(emptyEmail);
    } catch {
      // Ver arriba: no bloquea el reintento.
    }
  }
  window.location.href = getGbusinessConnectUrl(ctaId, null, { otraCuenta: true });
}
